import React, { useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { BrandColor } from '@styleseat/brand';
import { Box, Inline } from '@styleseat/ui';
import Label from './Label';
import Message from './Message';
import Stack from '../Stack';
import TextInput, { TextInputDefaultProps, TextInputProps } from '../TextInput/TextInput';
import Absolute from '../Absolute';
import BodyText from '../BodyText';
import { BadgeOptions } from '../../provider/settings/MenuItem/layout/types';

const DEFAULT_LEFT_PADDING = 12;

export type TextFormFieldProps = TextInputProps & {
  accentColor?: string;
  message?: React.ReactNode | string;
  label?: React.ReactNode | string;
  padding?: number;
  borderColor?: string;
  isLabelAccentColorOnMessage?: boolean;
  isMessageJustified?: boolean;
  messageTestID?: string;
  valueSuffix?: React.ReactNode;
  valuePrefix?: React.ReactNode;
  prefixWidth?: number;
  badgeOptions?: BadgeOptions | null;
};

/**
 * A Text Input Form Component implementation atop `TextInput`.
 * Includes options for a label above the field
 * and a message (i.e. error message) displayed below the field.
 */
const TextFormField: React.FCWithChildren<TextFormFieldProps> = React.forwardRef(
  ({
    message,
    label,
    borderColor,
    accentColor,
    isLabelAccentColorOnMessage,
    isMessageJustified,
    testID,
    messageTestID,
    valueSuffix,
    valuePrefix,
    prefixWidth,
    badgeOptions,
    ...restProps
  }, ref) => {
    const id = useMemo(() => `text-form-filed-${uuidv4()}`, []);
    const { style, disabled } = restProps;
    const extraPadding = valuePrefix ? prefixWidth : 0;
    // determine padding using the typical override rules and add in our extra padding for the
    // prefix, if any
    const paddingLeft = extraPadding + [
      style?.paddingLeft,
      style?.paddingX,
      style?.padding,
      DEFAULT_LEFT_PADDING,
    ].find(item => typeof item === 'number');

    let labelColor = isLabelAccentColorOnMessage && message ? accentColor : undefined;

    if (disabled) {
      labelColor = BrandColor.Ash;
    }

    return (
      <Box>
        <Stack space={4}>
          <Stack space={4}>
            <Inline space={8}>
              <Label
                label={label}
                color={labelColor}
                id={id}
                testID={testID}
              />

              {badgeOptions && (
                <Box
                  paddingX={8}
                  paddingY={4}
                  width={badgeOptions.width || undefined}
                  height={badgeOptions.height || undefined}
                  borderRadius={badgeOptions.borderRadius || 100}
                  color={badgeOptions.badgeColor}
                  alignItems="center"
                  justifyContent="center"
                  testID={`${testID}-badge`}
                >
                  <BodyText
                    color={badgeOptions.textColor}
                    appearance="smaller"
                  >
                    {badgeOptions.text}
                  </BodyText>
                </Box>
              )}
            </Inline>
            <Box>
              {valuePrefix ? (
                <Absolute
                  left={10}
                  bottom={0}
                  top={0}
                  justifyContent="center"
                >
                  <BodyText
                    color={disabled ? BrandColor.Metal : BrandColor.BackInBlack}
                  >
                    {valuePrefix}
                  </BodyText>
                </Absolute>
              ) : null}
              <TextInput
                {...restProps}
                style={{
                  ...style,
                  paddingLeft,
                }}
                ref={ref}
                appearance="form"
                accentColor={borderColor}
                id={id}
                testID={testID}
              />
              {valueSuffix ? (
                <Absolute
                  right={10}
                  bottom={0}
                  top={0}
                  justifyContent="center"
                >
                  <BodyText
                    color={disabled ? BrandColor.Metal : BrandColor.BackInBlack}
                  >
                    {valueSuffix}
                  </BodyText>
                </Absolute>
              ) : null}
            </Box>
          </Stack>
          <Message
            message={message}
            accentColor={disabled ? BrandColor.Ash : accentColor}
            isJustified={isMessageJustified}
            testID={messageTestID ? undefined : testID}
            testIDOverride={messageTestID}
          />
        </Stack>
      </Box>
    );
  },
);

TextFormField.defaultProps = {
  ...TextInputDefaultProps,
  valueSuffix: undefined,
  valuePrefix: undefined,
  prefixWidth: 10,
  type: 'text',
  appearance: 'bordered',
};

export default TextFormField;
